import { supabase } from "./supabase";
import type { Oekt, Rolle } from "./session";

/**
 * Deltakerne i produksjonen.
 *
 * ========  REDAKTØRENS, MED PRODLEDER OG PRODUSENT VED SIDEN AV  ========
 *
 * Avklart 27.10.2026, og det løste et spørsmål dokumentene hadde sagt tre
 * ulike ting om. Brukeren: «Når en redaktør setter opp et prosjekt, så skal
 * de få spørsmål om å legge inn deltakere. Kommer det nye deltakere, eller
 * redaktør ikke vil sette det opp ved første innlogging, så skal dette være
 * en arbeidsflate i menyen som redaktørene har tilgang til.»
 *
 * Alle LESER dem. Det må de: forkortelsen er det regi skriver i loggen, og
 * rapporten slår den opp for å vise hvem det var.
 *
 * ========  INGEN SLETTING, BARE EN SLUTTDATO  ========
 *
 * `su_question` og `su_answer` peker hit med `on delete cascade`. En sletting
 * ville tatt synkspørsmålene og svarene med seg — altså loggen fra en dag
 * ingen lenger kan se hva handlet om. `active_to` gjør deltakeren historisk
 * uten å ta noe bort, og det er samme form som medlemmer som slutter.
 */

export interface Deltaker {
  readonly id: string;
  readonly navn: string;
  readonly forkortelse: string;
  readonly farge: string | null;
  readonly sluttet: string | null;
}

/** Rollene som fører deltakerlista. Speiler policyen på `participant`. */
const FOERER: readonly Rolle[] = ["redaktor", "prodleder", "produsent"];

export function kanSkrive(oekt: Oekt): boolean {
  /* Ogsaa de ekstra fagfunksjonene — basen gjoer ingen forskjell. */
  return [oekt.member.role, ...(oekt.member.extraRoles ?? [])]
    .some((r) => FOERER.includes(r));
}

export async function hent(produksjonId: string): Promise<readonly Deltaker[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("participant")
    .select("id, name, abbreviation, color, active_to")
    .eq("production_id", produksjonId)
    .order("abbreviation", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]),
    navn: String(r["name"]),
    forkortelse: String(r["abbreviation"]),
    farge: r["color"] == null ? null : String(r["color"]),
    sluttet: r["active_to"] == null ? null : String(r["active_to"]),
  }));
}

/**
 * Forkortelsen er det som faktisk skrives i loggen.
 *
 * STORE BOKSTAVER, fordi det er slik ALI står i en loggpost — og fordi to
 * deltakere som heter «Ali» og «ALI» er én deltaker skrevet på to måter.
 */
export function normaliser(forkortelse: string): string {
  return forkortelse.trim().toUpperCase();
}

export async function leggTil(
  produksjonId: string, navn: string, forkortelse: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.from("participant").insert({
    production_id: produksjonId,
    name: navn.trim(),
    abbreviation: normaliser(forkortelse),
  });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

export async function endre(
  id: string, felt: { navn?: string; forkortelse?: string },
): Promise<boolean> {
  if (supabase === null) return true;
  const rad: Record<string, unknown> = {};
  if (felt.navn !== undefined) rad["name"] = felt.navn.trim();
  if (felt.forkortelse !== undefined) rad["abbreviation"] = normaliser(felt.forkortelse);
  if (Object.keys(rad).length === 0) return true;
  const { error } = await supabase.from("participant").update(rad).eq("id", id);
  return error === null;
}

/** Ut av produksjonen, eller inn igjen. Aldri bort. */
export async function settSluttet(id: string, sluttet: boolean): Promise<boolean> {
  if (supabase === null) return true;
  const { error } = await supabase
    .from("participant")
    .update({ active_to: sluttet ? new Date().toISOString().slice(0, 10) : null })
    .eq("id", id);
  return error === null;
}
